import { AppConst, UtilsConst } from '@/constants'
import { apiRequest, onApiCatchError } from '@/store/modules/ApiRequestHelper'
import { TenantContextDocument } from '@/store/modules/tenantcontext/types'
import { TenantHelpers } from '@/utils/tenant-helpers'
import Axios from 'axios'

export default {
  loadTenantContextDocuments(context: any, payload: { tenantId: number; page?: number; limit?: number }): Promise<boolean> {
    const requestParams = {
      ...payload,
      page: payload.page || 1,
      limit: payload.limit || 5,
    }
    const url = `${AppConst.APIEndpoint.assetV1Url}${AppConst.APIEndpoint.assetsReference}`

    return apiRequest(
      {
        action: UtilsConst.HTTPAction.GET,
        url,
        params: requestParams,
      },
      context,
      'updateTenantContextDocuments',
      'getting tenant context documents',
      '',
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: requestParams.tenantId,
      },
      (ctx: any, error: any, errorMsg: string, apiCatchError: any) => {
        if (error && error.response && error.response.status === 404) {
          ctx.commit('updateTenantContextDocuments', {
            references: [] as TenantContextDocument[],
            pagination: {
              page: requestParams.page,
              limit: requestParams.limit,
              totalRows: 0,
              totalPages: 1,
            },
          })
          return Promise.resolve(true)
        } else {
          apiCatchError(ctx, error, errorMsg)
          return Promise.resolve(false)
        }
      }
    )
  },

  presignTenantContextFile(context: any, payload: { tenantId: number; filename: string; mime: string; size: number }) {
    // Context document upload is a 3-step flow:
    // 1) presign: request temporary upload target + storage metadata
    // 2) upload: send bytes directly to storage (multipart form POST or direct PUT)
    // 3) complete: notify Asset API to finalize/index the uploaded file as a tenant context reference.
    // This split keeps large file transfer off the API while preserving tenant-scoped authorization.
    return apiRequest(
      {
        action: UtilsConst.HTTPAction.POST,
        url: `${AppConst.APIEndpoint.assetV1Url}${AppConst.APIEndpoint.assetPresigned}`,
        params: payload,
      },
      context,
      '',
      'presign tenant context document file',
      // Upload progress is handled inside UploadDocumentsDialog.
      // Avoid toggling global page loading (`isPreparingApp`) for each file step.
      '',
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: payload.tenantId,
      }
    )
  },

  async uploadTenantContextFile(context: any, payload: any) {
    const { mode, storage, file, assetGuid } = payload

    let url = ''

    if (mode === 'multipart') {
      url = payload[mode].action_url
    } else {
      url = payload[mode].url
    }

    const completeRequestPayload = {
      ...storage,
      assetGuid,
      assetType: 'document',
      originalFilename: file.name,
      mimeType: file.type,
      sizeBytes: file.size,
      uploadMode: mode,
    }

    try {
      if (mode === 'multipart') {
        // Multipart mode (form POST) is used when backend returns fields + action_url.
        const formData = new FormData()
        const fields = payload[mode].fields

        if (fields) {
          Object.keys(fields).forEach(key => {
            formData.append(key, fields[key])
          })
        }

        formData.append('file', file)

        return await Axios.post(
          url,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            }
          }
        ).then((uploadResponse) => ({
          status: uploadResponse.status,
          data: {
            ...completeRequestPayload,
            etag: (uploadResponse.headers && uploadResponse.headers.etag) || '',
          },
        })).catch(() => {
          onApiCatchError(context, Error(), 'Something went wrong when uploading tenant context document (multipart).')
          return false
        })
      } else {
        // Direct PUT mode is used for storage targets that provide upload URL + content-type headers.
        return await Axios.put(
          url,
          file,
          {
            headers: {
              'Content-Type': payload[mode].headers['Content-Type'][0],
            }
          }
        ).then((uploadResponse) => ({
          status: uploadResponse.status,
          data: {
            ...completeRequestPayload,
            etag: (uploadResponse.headers && uploadResponse.headers.etag) || '',
          },
        })).catch(() => {
          onApiCatchError(context, Error(), 'Something went wrong when uploading tenant context document.')
          return false
        })
      }
    } catch (error: any) {
      onApiCatchError(context, error, (error && error.message) || 'Something went wrong when uploading tenant context document.')
      return false
    }
  },

  completeTenantContextFileUpload(context: any, payload: any) {
    const tenantId = payload && payload.tenantId ? payload.tenantId : this.getMyTenantID(context.rootState.app)
    if (tenantId == null || tenantId <= 0 || tenantId === TenantHelpers.InvalidTenantID) {
      return Promise.reject('Invalid tenant ID for completeTenantContextFileUpload')
    }

    return apiRequest(
      {
        action: UtilsConst.HTTPAction.POST,
        url: `${AppConst.APIEndpoint.assetV1Url}${AppConst.APIEndpoint.assetsComplete}`,
        body: payload,
      },
      context,
      '',
      'complete tenant context document file upload',
      // Upload completion should not trigger tenant-page spinner between files.
      '',
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      }
    )
  },

  deleteTenantContextDocument(context: any, assetGuid: string): Promise<boolean> {
    const tenantId = this.getMyTenantID(context.rootState.app)
    if (tenantId == null || tenantId <= 0 || tenantId === TenantHelpers.InvalidTenantID) {
      return Promise.reject('Invalid tenant ID for deleteTenantContextDocument')
    }

    return apiRequest(
      {
        action: UtilsConst.HTTPAction.DELETE,
        url: `${AppConst.APIEndpoint.assetV1Url}${AppConst.APIEndpoint.assetsReference}/${assetGuid}`,
      },
      context,
      '',
      `deleting tenant context document, assetGuid: ${assetGuid}`,
      // Keep delete flow on local table overlay; avoid global page-loading
      // state that can remount tab content and show stale rows briefly.
      '',
      {
        [UtilsConst.RequestHeaders.CustomHeaderKeyTenantId]: tenantId,
      }
    )
  },
}
